import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail, ChevronDown } from "lucide-react";
import { useSite } from "@/context/AppContext";
import { Container } from "./Ui";

const NAV = [
  { to: "/galleria", label: "Prima e dopo" },
  { to: "/il-centro", label: "Il centro" },
  { to: "/contatti", label: "Contatti" },
];
const navCls = ({ isActive }) => `text-sm transition-colors hover:text-[var(--petrol)] ${isActive ? "text-[var(--petrol)]" : ""}`;

function Logo() {
  return (
    <Link to="/" className="group flex items-center gap-3" data-testid="header-logo" aria-label="Morphe Aesthetic – Home">
      <img src="/brand/rosa-256.png" alt="" width={146} height={256} className="h-10 w-auto transition-transform duration-500 group-hover:-rotate-6 sm:h-12" />
      <img src="/brand/logo-morphe.webp" alt="Morphe Aesthetic" width={1997} height={192} className="h-[14px] w-auto sm:h-5 xl:h-6" />
    </Link>
  );
}

function TreatmentsMenu() {
  const { catalog } = useSite();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <div className="flex items-center">
        <NavLink to="/trattamenti" className="py-2 text-sm hover:text-[var(--petrol)]" data-testid="nav-trattamenti">Trattamenti</NavLink>
        <button aria-expanded={open} aria-label="Apri categorie trattamenti" className="p-1" onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)} data-testid="nav-trattamenti-toggle">
          <ChevronDown size={14} />
        </button>
      </div>
      {open && (
        <div className="absolute left-0 top-full z-50 w-72 rounded-2xl border border-[var(--line)] bg-white p-3 shadow-xl">
          {(catalog?.categories || []).map((c) => (
            <Link key={c.id} to={`/trattamenti/${c.slug}`} onClick={() => setOpen(false)} className="block rounded-xl px-4 py-3 text-sm hover:bg-[var(--ivory-soft)]" data-testid={`nav-cat-${c.slug}`}>{c.name}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-ivory/90 backdrop-blur-xl">
      <Container className="flex h-20 items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Principale" className="hidden items-center gap-7 lg:flex">
          <NavLink to="/" end className={navCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/chi-siamo" className={navCls} data-testid="nav-chi-siamo">Chi siamo</NavLink>
          <TreatmentsMenu />
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={navCls} data-testid={`nav-${n.to.slice(1)}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden md:block"><Link to="/prenota" className="btn-primary !py-2.5 text-sm" data-testid="header-book-btn">Prenota</Link></span>
          <button className="rounded-full p-2 lg:hidden" aria-label={open ? "Chiudi menu" : "Apri menu"} aria-expanded={open} onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </Container>
      {open && (
        <nav aria-label="Menu mobile" className="border-t border-[var(--line)] bg-[var(--ivory)] lg:hidden" data-testid="mobile-menu">
          <Container className="flex flex-col py-4">
            {[{ to: "/", label: "Home" }, { to: "/chi-siamo", label: "Chi siamo" }, { to: "/trattamenti", label: "Trattamenti" }, ...NAV, { to: "/prenota", label: "Prenota" }].map((n) => (
              <Link key={n.to} to={n.to} className="border-b border-[var(--line)] py-4 font-serif text-2xl">{n.label}</Link>
            ))}
          </Container>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { site } = useSite();
  const s = site || {};
  return (
    <footer className="bg-[var(--ink)] text-[var(--ivory)]">
      <Container className="grid gap-12 py-16 md:grid-cols-4">
        <div className="md:col-span-2">
          <img src="/brand/logo-morphe.webp" alt={s.center_name || "Morphe Aesthetic"} width={1997} height={192} loading="lazy" className="h-6 w-auto brightness-0 invert" />
          <p className="mt-3 max-w-sm text-sm text-ivory/75">{s.tagline}</p>
          <ul className="mt-6 space-y-2 text-sm text-ivory/85">
            <li className="flex gap-2"><MapPin size={16} className="mt-0.5 text-[var(--brass)]" />{s.address_street}, {s.address_zip} {s.address_city} ({s.address_province})</li>
            <li className="flex gap-2"><Phone size={16} className="mt-0.5 text-[var(--brass)]" />{s.phone}</li>
            <li className="flex gap-2"><Mail size={16} className="mt-0.5 text-[var(--brass)]" />{s.email}</li>
          </ul>
        </div>
        <div>
          <p className="eyebrow !text-[var(--brass)]">Esplora</p>
          <ul className="mt-4 space-y-2 text-sm">
            {[["/trattamenti", "Trattamenti"], ["/listino-prezzi", "Listino prezzi"], ["/galleria", "Prima e dopo"], ["/guide", "Guide"], ["/glossario", "Glossario"], ["/faq", "FAQ"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="text-ivory/80 hover:text-white">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="eyebrow !text-[var(--brass)]">Informazioni</p>
          <ul className="mt-4 space-y-2 text-sm">
            {[["/privacy", "Privacy"], ["/cookie", "Cookie"], ["/termini", "Termini"], ["/mappa-del-sito", "Mappa del sito"], ["/prenotazione/gestisci", "Gestisci prenotazione"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="text-ivory/80 hover:text-white">{l}</Link></li>
            ))}
          </ul>
        </div>
      </Container>
      <Container className="border-t border-white/10 py-6 text-xs text-ivory/60">
        {s.company_name} · P.IVA {s.vat_number} · Direttore sanitario: {s.medical_director}
      </Container>
    </footer>
  );
}

function CookieNotice() {
  const [show, setShow] = useState(() => !localStorage.getItem("ma_cookie_notice"));
  if (!show) return null;
  return (
    <div role="region" aria-label="Informativa cookie" className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-xl rounded-2xl border border-[var(--line)] bg-white p-5 shadow-2xl" data-testid="cookie-banner">
      <p className="text-sm">Questo sito usa solo cookie tecnici necessari. Nessun cookie di tracciamento o pubblicitario è attivo. Contenuti di terze parti (es. mappa) vengono caricati solo su tua richiesta. <Link to="/cookie" className="underline">Cookie policy</Link></p>
      <button className="btn-primary mt-4 !py-2 text-sm" onClick={() => { localStorage.setItem("ma_cookie_notice", "1"); setShow(false); }} data-testid="cookie-accept-btn">Ho capito</button>
    </div>
  );
}

function DemoBar() {
  const { site } = useSite();
  if (!site?.demo_notice) return null;
  return (
    <div className="bg-[var(--powder-light)] py-2 text-center text-xs text-[var(--petrol-dark)]" data-testid="demo-notice">
      Sito in allestimento: contenuti, prezzi e dati tra [parentesi quadre] sono dimostrativi e da sostituire.
    </div>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-[var(--petrol)] focus:px-4 focus:py-2 focus:text-white">Vai al contenuto</a>
      <DemoBar />
      <Header />
      <main id="main" tabIndex={-1}><Outlet /></main>
      <Footer />
      <CookieNotice />
    </>
  );
}
